import { AddToFavoritesButton } from "components/AddToFavoriteButton";
import { ButtonSecondary } from "components/Clickable/Buttons";
import { AttributeKeyValue } from "components/FilteredDropdown";
import { usePartiallyConfiguredProduct } from "components/productContext";
import {
    AttributeOptionDetails,
    AttributeOptionPlusInfo,
} from "interfaces/products";
import { useRouter } from "next/router";
import Checkmark from "public/svg/checkmark.svg";
import WishlistIcon from "public/svg/wishlist.svg";
import { useState } from "react";

import { ProductCodeConfigurator } from ".";
import { buildNewQuery, urlSearchParamsToQueryStringInput } from "./helpers";

import styles from "./product-code-configurator.module.scss";

interface Props {
    attribute: AttributeOptionDetails;
    selectedOptions: AttributeOptionPlusInfo[];
    loading: boolean;
    setLoading: (loading: boolean) => void;
}

export const PDPProductCodeConfigurator = ({
    attribute,
    selectedOptions,
    loading,
    setLoading,
}: Props) => {
    const router = useRouter();

    const onChange = (attributeKeyValue: AttributeKeyValue) => {
        setLoading(true);
        const currentQuery = new URLSearchParams(window.location.search);
        const newQuery = urlSearchParamsToQueryStringInput(
            buildNewQuery(
                currentQuery,
                attribute.configurator,
                attributeKeyValue,
            ),
        );

        /**
         * TODO harden this? Will there ever be anything other than `slug` and
         * my search params?
         */
        router
            .replace(
                { query: { slug: router.query.slug, ...newQuery } },
                undefined,
                {
                    scroll: false,
                },
            )
            .then(() => setLoading(false));
    };

    return (
        <ProductCodeConfigurator
            placement="inline"
            details={attribute}
            selectedOptions={selectedOptions}
            onChange={onChange}
            loading={loading}
            actionButtons={<ActionButtons />}
        />
    );
};

const ActionButtons = () => {
    const [copied, setCopied] = useState(false);
    const { productCodes } = usePartiallyConfiguredProduct();

    async function copyTextToClipboard(text: string) {
        if (!navigator.clipboard || !window.isSecureContext) {
            return;
        }

        return await navigator.clipboard.writeText(text);
    }

    const copyProductCode = () =>
        copyTextToClipboard(productCodes.join(", "))
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch((error) =>
                console.log("Failed to copy product code: " + error),
            );

    return (
        <>
            {productCodes.length <= 2 && (
                <ButtonSecondary
                    className={styles.copyButton}
                    onClick={copyProductCode}
                >
                    {copied ? "Copied" : "Copy"}
                    {copied ? (
                        <Checkmark className={styles.checkmark} />
                    ) : (
                        <WishlistIcon />
                    )}
                </ButtonSecondary>
            )}
            <AddToFavoritesButton />
        </>
    );
};
